import * as React from "react";

type Theme = "setu-light" | "setu-dark" | "light" | "dark";

type ThemeProviderProps = {
    children: React.ReactNode;
    defaultTheme?: Theme;
    storageKey?: string;
};

type ThemeProviderState = {
    theme: Theme;
    setTheme: (theme: Theme) => void;
};

const ThemeProviderContext = React.createContext<ThemeProviderState | undefined>(undefined);

export function ThemeProvider({
    children,
    defaultTheme = "setu-light",
    storageKey = "setu-ui-theme",
    ...props
}: ThemeProviderProps) {
    const [theme, setTheme] = React.useState<Theme>(() => {
        if (typeof window !== "undefined") {
            const stored = localStorage.getItem(storageKey) as Theme;
            // Priority: localStorage > defaultTheme > 'setu-light'
            // This ensures user's saved preference is always respected
            const finalTheme = stored || defaultTheme;

            return finalTheme;
        }
        return defaultTheme;
    });

    React.useLayoutEffect(() => {
        const root = window.document.documentElement;

        // Remove all theme classes first
        root.classList.remove("light", "dark", "setu");

        // Apply the appropriate theme class
        if (theme === "setu-light") {
            root.classList.add("setu");
        } else if (theme === "setu-dark") {
            root.classList.add("setu", "dark");
        } else {
            root.classList.add(theme);
        }
    }, [theme, storageKey]);

    const value = React.useMemo(
        () => ({
            theme,
            setTheme: (newTheme: Theme) => {
                if (typeof window !== "undefined") {
                    // Persist theme preference to localStorage for future visits
                    localStorage.setItem(storageKey, newTheme);
                }
                setTheme(newTheme);
            },
        }),
        [theme, storageKey]
    );

    return (
        <ThemeProviderContext.Provider {...props} value={value}>
            {children}
        </ThemeProviderContext.Provider>
    );
}

export const useTheme = () => {
    const context = React.useContext(ThemeProviderContext);

    if (context === undefined) {
        throw new Error("useTheme must be used within a ThemeProvider");
    }

    return context;
};

export type { Theme, ThemeProviderProps, ThemeProviderState };
